import { pwaConfig } from "./pwa-config.ts";

export type ProductionBuildKind = "acceptance" | "release";

export const serviceWorkerScriptURL = (version: string): string =>
  `${pwaConfig.serviceWorkerPath}?version=${encodeURIComponent(version)}`;

export const serviceWorkerIdentifier = (
  version: string,
  buildKind: ProductionBuildKind,
  buildId = version,
): string => `snakely-${buildKind}-${version}-${buildId}`;

const validateAssets = (assets: readonly string[]): readonly `/${string}`[] => {
  if (
    assets.length === 0 ||
    !assets.includes("/") ||
    assets.some((path) => !path.startsWith("/") || path.startsWith("//"))
  ) {
    throw new Error("Service worker assets must be local absolute paths");
  }
  const normalized = [...new Set(assets)].sort();
  if (normalized.length !== assets.length) {
    throw new Error("Service worker assets must be unique");
  }
  return normalized as readonly `/${string}`[];
};

export const createServiceWorkerSource = (
  version: string,
  buildKind: ProductionBuildKind,
  buildId = version,
  assets: readonly string[] = ["/"],
): string => {
  if (buildId.trim().length === 0) {
    throw new Error("Service worker build ID must be non-empty");
  }
  const identifier = serviceWorkerIdentifier(version, buildKind, buildId);
  const cachePrefix = "snakely-";
  const cachedAssets = validateAssets(assets);
  return [
    `const SNAKELY_SERVICE_WORKER = ${JSON.stringify(identifier)};`,
    `const SNAKELY_VERSION = ${JSON.stringify(version)};`,
    `const SNAKELY_CACHE_PREFIX = ${JSON.stringify(cachePrefix)};`,
    `const SNAKELY_CACHE = ${JSON.stringify(identifier)};`,
    `const SNAKELY_ASSETS = ${JSON.stringify(cachedAssets)};`,
    'self.addEventListener("install", (event) => { event.waitUntil(caches.open(SNAKELY_CACHE).then((cache) => cache.addAll(SNAKELY_ASSETS)).then(() => self.skipWaiting())); });',
    'self.addEventListener("activate", (event) => { event.waitUntil(caches.keys().then((names) => Promise.all(names.filter((name) => name.startsWith(SNAKELY_CACHE_PREFIX) && name !== SNAKELY_CACHE).map((name) => caches.delete(name)))).then(() => self.clients.claim())); });',
    'self.addEventListener("fetch", (event) => { const request = event.request; const url = new URL(request.url); if (request.method !== "GET" || url.origin !== self.location.origin) { return; } const cacheKey = request.mode === "navigate" ? "/" : url.pathname; event.respondWith(caches.open(SNAKELY_CACHE).then((cache) => cache.match(cacheKey)).then((response) => response ?? fetch(request).catch(() => Response.error()))); });',
    "void SNAKELY_SERVICE_WORKER;",
    "void SNAKELY_VERSION;",
    "",
  ].join("\n");
};
